'use client';

import { Box, Paper, Typography } from '@mui/material';
import React from 'react';

/**
 * Card Component - Molecule Design Pattern
 *
 * A card component that combines atoms to create a reusable card layout.
 * Contains header, content, and optional footer sections.
 *
 * @param title - Card title
 * @param children - Card content
 * @param variant - Card variant (elevated, outlined, filled)
 * @param hoverable - Whether the card should have hover effects
 * @param onClick - Click handler for the card
 */
export interface CardProps {
  title?: string;
  children: React.ReactNode;
  variant?: 'elevated' | 'outlined' | 'filled';
  hoverable?: boolean;
  onClick?: () => void;
  sx?: any;
}

export default function Card({ children, variant, sx, ...props }: CardProps) {
  const getVariantProps = () => {
    switch (variant) {
      case 'outlined':
        return { variant: 'outlined' as const };
      case 'filled':
        return {
          sx: {
            backgroundColor: 'grey.50',
            ...sx,
          },
        };
      default:
        return {
          elevation: 1,
          sx: {
            ...sx,
            ...(props.hoverable && {
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }),
          },
        };
    }
  };

  return (
    <Paper
      onClick={props.onClick}
      sx={{
        cursor: props.onClick ? 'pointer' : 'default',
        overflow: 'hidden',
        ...getVariantProps().sx,
      }}
      {...(variant === 'outlined' ? { variant: 'outlined' } : { elevation: 1 })}
    >
      {/* Card Header */}
      {props.title && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Typography
            variant='h6'
            sx={{
              fontWeight: 'semibold',
              mb: 0.5,
            }}
          >
            {props.title}
          </Typography>
        </Box>
      )}

      {/* Card Content */}
      <Box sx={{ px: 3, py: 2 }}>{children}</Box>
    </Paper>
  );
}
